import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  Divider,
  Badge,
  Link,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Save,
  Article,
  PlaylistAddCheck,
  Download,
} from "@mui/icons-material";
import {
  OperationLogService,
  RegisterTestTargetsRequest,
  RegisterTestTargetsResponse,
  TestTarget as OperationLogTestTarget,
  GenerateFromOperationLogResponse,
} from "../services/operationLogService";
import { TestTarget as AppTestTarget } from "../types/TestTarget";
import { isGenerationResultExportImportEnabled } from "../config/featureFlags";
import { SpecRegistrationHierarchicalView } from "./SpecRegistrationHierarchicalView";
import type { DeletionCandidate } from "../utils/spec-import/types";
import { deletionCandidatesToPageResults } from "../utils/spec-import/deletionCandidatesToPageResults";

// page単位の結果データ構造
interface PageResult {
  group_index: number;
  path: string;
  test_targets: OperationLogTestTarget[];
  usecases: any[];
  is_new_view?: boolean;
  view_id?: string;  // View項目のID（ユースケース登録時に使用）
  /** 仕様書インポート時の削除対象 Service ページ */
  is_deletion_page?: boolean;
}

interface TestTargetRegistrationDialogProps {
  open: boolean;
  onClose: () => void;
  onRegistrationComplete: (result: RegisterTestTargetsResponse) => void;
  targets: PageResult[];
  existingTargets: AppTestTarget[];
  workspaceId: string;
  projectId: string;
  operationLogService: OperationLogService;
  traceId?: string;  // AIジョブモニタリング用トレースID
  traceparent?: string;  // W3C Trace Context
  /** 生成時の補足（例: トークン制限のためpagesを省略して再実行した旨） */
  generationNote?: string;
  /** generation_note内のリンク先URL（{{MANUAL_LINK}}の差し替え用） */
  generationNoteLinkUrl?: string;
  // userToken removed - using Auth0 authentication
  /**
   * 仕様書インポートのように streamlit を介さず直接 SSTF に登録したい場合に渡す。
   * 渡された場合、handleRegister は operationLogService.registerTestTargets ではなくこの関数を呼ぶ。
   * 渡されない場合は既存の operationLogService 経路（操作ログ / JSON 読み込みインポート）を使う。
   */
  customRegisterHandler?: (
    request: RegisterTestTargetsRequest & {
      deleteCandidateIds?: Set<string>;
      existingChildrenOf?: Record<string, string[]>;
    },
  ) => Promise<RegisterTestTargetsResponse>;
  /**
   * 仕様書インポートのように階層構造で表示したい場合に "hierarchical" を渡す。
   * "hierarchical" のとき本体は SpecRegistrationHierarchicalView で木構造を描画する。
   * 既定 "flat"。操作ログ / 生成結果 JSON インポートは flat の一覧表示のまま。
   */
  viewMode?: "flat" | "hierarchical";
  /**
   * ユースケースタブを非表示にする。仕様書インポートのように usecases が常に空の場合に true。
   * 既定 false（操作ログ / 生成結果 JSON は表示）。
   */
  hideUsecasesTab?: boolean;
  /**
   * 仕様書インポート時の削除候補（仕様書に登場しない既存ターゲット）。
   * 渡された場合、operation:"DELETE" のノードとしてツリーに表示し、
   * ユーザーがチェックしたものを handleRegister 時に削除する。
   */
  deletionCandidates?: DeletionCandidate[];
  /**
   * 削除時に子孫を葉→親順で削除するための親子マップ（parentId → 子 id[]）。
   * deletionCandidates と合わせて渡す。
   */
  existingChildrenOf?: Record<string, string[]>;
  /**
   * 仕様書インポート（viewMode="hierarchical"）時に「戻る」ボタンを表示するためのコールバック。
   * 渡された場合のみダイアログ下部に「戻る」ボタンを表示する。
   */
  onBack?: () => void;
}

// page単位データをフラット化。同一 test_target_id はページ走査順で最後の出現を採用（API と整合）
function flattenPageResults(
  pageResults: PageResult[]
): OperationLogTestTarget[] {
  const out: OperationLogTestTarget[] = [];
  const indexById = new Map<string, number>();

  pageResults.forEach((page) => {
    if (!page.test_targets || !Array.isArray(page.test_targets)) return;
    page.test_targets.forEach((target) => {
      const id = target.test_target_id;
      if (!id) {
        out.push(target);
        return;
      }
      const idx = indexById.get(id);
      if (idx === undefined) {
        indexById.set(id, out.length);
        out.push(target);
      } else {
        out[idx] = target;
      }
    });
  });

  return out;
}

export function hasRegistrableContent(pageResults: PageResult[]): boolean {
  return (
    flattenPageResults(pageResults).length > 0 ||
    pageResults.some((page) => !!page.usecases && page.usecases.length > 0)
  );
}

function dedupe_targets_by_id_last_wins(
  targets: OperationLogTestTarget[]
): OperationLogTestTarget[] {
  const out: OperationLogTestTarget[] = [];
  const indexById = new Map<string, number>();
  for (const target of targets) {
    const id = target.test_target_id;
    if (!id) {
      out.push(target);
      continue;
    }
    const idx = indexById.get(id);
    if (idx === undefined) {
      indexById.set(id, out.length);
      out.push(target);
    } else {
      out[idx] = target;
    }
  }
  return out;
}

/**
 * 既存ターゲットの親ID（GraphQL 変換では `parentId` のみ設定され、`parent_test_target_id` は使われない）
 */
function getExistingParentId(existing: AppTestTarget): string | undefined {
  const v = existing.parentId ?? existing.parent_test_target_id;
  if (v === "" || v == null) return undefined;
  return v;
}

export const TestTargetRegistrationDialog: React.FC<
  TestTargetRegistrationDialogProps
> = ({
  open,
  onClose,
  onRegistrationComplete,
  targets,
  existingTargets,
  workspaceId,
  projectId,
  operationLogService,
  traceId,
  traceparent,
  generationNote,
  generationNoteLinkUrl,
  // userToken removed - using Auth0 authentication
  customRegisterHandler,
  viewMode = "flat",
  hideUsecasesTab = false,
  deletionCandidates,
  existingChildrenOf,
  onBack,
}) => {
  const [selectedTargets, setSelectedTargets] = useState<
    OperationLogTestTarget[]
  >([]);
  const [selectedUsecases, setSelectedUsecases] = useState<Set<string>>(
    new Set()
  ); // ユースケースの選択状態（usecase_idのSet）
  /** CREATE→既存UPDATE 切替（#882）。pageResults は props 由来のため上書き state で保持 */
  const [usecaseCreateSwitches, setUsecaseCreateSwitches] = useState<
    Array<{
      groupIndex: number;
      originalCreateId: string;
      originalUsecase: any;
      existingUsecaseId: string;
    }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrationResult, setRegistrationResult] = useState<RegisterTestTargetsResponse | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [deleteCandidateIds, setDeleteCandidateIds] = useState<Set<string>>(new Set());
  const logged_param_mismatch = useRef<Set<string>>(new Set());

  // 2カラムレイアウト用の状態
  const [selectedPageIndex, setSelectedPageIndex] = useState<number | null>(
    null
  );
  const [currentTab, setCurrentTab] = useState<number>(0); // 0: 項目, 1: ユースケース

  // 登録中はタブ/ウィンドウを閉じる誤操作を抑止する
  useEffect(() => {
    if (!loading) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [loading]);

  // 既存データとの差分を検出する関数
  const findExistingTarget = (targetId: string): AppTestTarget | null => {
    return existingTargets.find((target) => target.id === targetId) || null;
  };

  const normalize_empty_text = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    const text = String(value);
    return text.trim() === "" ? "" : text;
  };

  /** 親ターゲットを「名前(ID)」形式で表示（同一バッチ→既存データの順で名前解決） */
  const get_parent_display_label = (
    parent_test_target_id: string | undefined
  ): string => {
    const pid = parent_test_target_id?.trim();
    if (!pid) return "-";
    const from_batch = selectedTargets.find((t) => t.test_target_id === pid);
    const batch_name = from_batch?.name?.trim();
    const existing = findExistingTarget(pid);
    const existing_name = existing?.name?.trim();
    const label =
      batch_name && batch_name !== ""
        ? batch_name
        : existing_name && existing_name !== ""
          ? existing_name
          : "無名";
    return `${label}(${pid})`;
  };

  // システム管理フィールドのリスト（比較から除外）
  const SYSTEM_MANAGED_FIELDS = new Set([
    "status", // レビューステータス
    "isManual",
    "is_manual", // 手動実行フラグ
    "failCount",
    "fail_count", // 連続失敗回数
    "isDownload",
    "is_download", // ダウンロードフラグ
    "customTypeFlg", // カスタムタイプフラグ
    "forceAnalyze",
    "force_analyze", // 強制分析フラグ
    "automationStats", // 自動実行成功率情報
  ]);

  // システム管理フィールドを除外してオブジェクトをコピーする関数
  const excludeSystemFields = (obj: any): any => {
    if (obj === null || obj === undefined) {
      return obj;
    }
    if (Array.isArray(obj)) {
      return obj.map((item) => {
        if (typeof item === "object" && item !== null) {
          // transitions配列内のオブジェクトからstatusを除外
          const cleaned: any = {};
          Object.keys(item).forEach((key) => {
            if (key !== "status") {
              cleaned[key] = excludeSystemFields(item[key]);
            }
          });
          return cleaned;
        }
        return item;
      });
    }
    if (typeof obj === "object") {
      const cleaned: any = {};
      Object.keys(obj).forEach((key) => {
        if (!SYSTEM_MANAGED_FIELDS.has(key)) {
          cleaned[key] = excludeSystemFields(obj[key]);
        }
      });
      return cleaned;
    }
    return obj;
  };

  // paramsを深く比較する関数（バックエンドと同じロジック）
  // キーの順序を無視してJSON文字列化して比較（Pythonのsort_keys=True相当）
  // システム管理フィールドは除外して比較
  const deepCompareParams = (params1: any, params2: any): boolean => {
    try {
      const is_valid_value = (value: any): boolean => {
        if (value === null || value === undefined) return false;
        if (typeof value === "string" && value.trim() === "") return false;
        return true;
      };

      const build_comparable_params = (new_params: any, existing_params: any) => {
        const cleaned_new = excludeSystemFields(new_params || {});
        const cleaned_existing = excludeSystemFields(existing_params || {});
        const comparable_new: any = {};
        const comparable_existing: any = {};

        Object.keys(cleaned_new).forEach((key) => {
          const new_value = cleaned_new[key];
          if (!is_valid_value(new_value)) {
            return;
          }
          comparable_new[key] = new_value;
          if (key in cleaned_existing) {
            comparable_existing[key] = cleaned_existing[key];
          }
        });

        return { comparable_new, comparable_existing };
      };

      // オブジェクトを再帰的にソートしてJSON文字列化する関数
      const sortKeysRecursively = (obj: any): any => {
        if (obj === null || obj === undefined) {
          return obj;
        }
        if (Array.isArray(obj)) {
          return obj.map(sortKeysRecursively);
        }
        if (typeof obj === "object") {
          const sorted: any = {};
          Object.keys(obj)
            .sort()
            .forEach((key) => {
              sorted[key] = sortKeysRecursively(obj[key]);
            });
          return sorted;
        }
        return obj;
      };

      const { comparable_new, comparable_existing } = build_comparable_params(
        params1,
        params2
      );

      // ソートして比較
      const sortedParams1 = sortKeysRecursively(comparable_new);
      const sortedParams2 = sortKeysRecursively(comparable_existing);
      return (
        JSON.stringify(sortedParams1) !== JSON.stringify(sortedParams2)
      );
    } catch (error) {
      // 比較に失敗した場合は変更ありとみなす（安全側）
      console.warn("params比較でエラーが発生しました:", error);
      return true;
    }
  };

  const { filteredPageResults, initialSelectedTargets } = useMemo(() => {
    const filterDeleteOperations = (target: OperationLogTestTarget) =>
      target.operation !== "DELETE";

    const filterUnchangedTargets = (target: OperationLogTestTarget): boolean => {
      if (target.operation !== "UPDATE") {
        return true;
      }

      const existing = findExistingTarget(target.test_target_id || "");
      if (!existing) {
        return true;
      }

      const nameChanged = target.name !== existing.name;
      const typeChanged =
        target.type?.toLowerCase() !== existing.type?.toLowerCase();
      const descriptionChanged =
        normalize_empty_text(target.description) !==
        normalize_empty_text(existing.description);
      const parentChanged =
        target.parent_test_target_id !== getExistingParentId(existing);

      const paramsChanged = deepCompareParams(target.params, existing.params);

      const hasChanges =
        nameChanged || typeChanged || descriptionChanged || parentChanged || paramsChanged;

      return hasChanges;
    };

    const filtered = targets
      .map((page) => ({
        ...page,
        test_targets: page.test_targets
          .filter(filterDeleteOperations)
          .filter(viewMode === "hierarchical" ? () => true : filterUnchangedTargets),
      }))
      .filter(
        (page) =>
          page.test_targets.length > 0 ||
          (page.usecases && page.usecases.length > 0)
      );

    const selected = flattenPageResults(filtered).map((target) => ({
      ...target,
      selected:
        viewMode === "hierarchical" ? filterUnchangedTargets(target) : true,
    }));

    return { filteredPageResults: filtered, initialSelectedTargets: selected };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets, viewMode, existingTargets]);

  const pageResults = useMemo<PageResult[]>(() => {
    const deletionPages =
      deletionCandidates && deletionCandidates.length > 0
        ? deletionCandidatesToPageResults(
            deletionCandidates,
            filteredPageResults.length
          )
        : [];
    return [...filteredPageResults, ...deletionPages];
  }, [filteredPageResults, deletionCandidates]);

  useEffect(() => {
    setSelectedTargets(initialSelectedTargets);

    const allUsecaseIds = new Set<string>();
    filteredPageResults.forEach((page) => {
      if (page.usecases && Array.isArray(page.usecases)) {
        page.usecases.forEach((uc) => {
          if (uc.usecase_id) {
            allUsecaseIds.add(uc.usecase_id);
          }
        });
      }
    });
    setSelectedUsecases(allUsecaseIds);

    setUsecaseCreateSwitches([]);

    if (deletionCandidates && deletionCandidates.length > 0) {
      setDeleteCandidateIds(new Set(deletionCandidates.map((c) => c.id)));
    } else {
      setDeleteCandidateIds(new Set());
    }

    setSelectedPageIndex(pageResults.length > 0 ? 0 : null);
  }, [filteredPageResults, initialSelectedTargets, pageResults, deletionCandidates]);

  const diff_box_base_sx = {
    fontSize: "0.75rem",
    p: 1,
    borderRadius: 1,
    border: "1px solid",
    width: "100%",
    boxSizing: "border-box",
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
    color: "text.primary",
  } as const;

  const diff_box_old_sx = {
    backgroundColor: "error.50",
    borderColor: "error.main",
  } as const;

  const diff_box_new_sx = {
    backgroundColor: "success.50",
    borderColor: "success.main",
  } as const;

  const format_field_value = (value: unknown): string => {
    if (value === null || value === undefined || value === "") {
      return "(空)";
    }
    return String(value);
  };

  const get_description_text = (target: OperationLogTestTarget): string => {
    if (target.description === null || target.description === undefined) {
      return "(空)";
    }
    if (typeof target.description === "string" && target.description === "") {
      return "(空)";
    }
    return String(target.description);
  };

  const format_json_value = (value: unknown): string => {
    if (value === undefined) return "(空)";
    try {
      const json = JSON.stringify(value, null, 2);
      return json ?? "(空)";
    } catch {
      return String(value);
    }
  };

  const render_diff_box = (
    label: "旧" | "新",
    value: string,
    tone: "old" | "new",
    is_last: boolean
  ) => {
    const tone_sx = tone === "old" ? diff_box_old_sx : diff_box_new_sx;
    const prefix = label === "旧" ? "❌ 旧: " : "✅ 新: ";
    return (
      <Box
        component="pre"
        sx={{
          ...diff_box_base_sx,
          ...tone_sx,
          mb: is_last ? 0 : 0.5,
        }}
      >
        {prefix}
        {value}
      </Box>
    );
  };

  type Field_change = {
    label: string;
    old_value: string | number | null | undefined;
    new_value: string | number | null | undefined;
  };

  type Target_change_summary = {
    has_changes: boolean;
    change_labels: string[];
    field_changes: Field_change[];
    params_changed: boolean;
    usecases_changed: boolean;
  };

  const get_target_change_summary = (
    target: OperationLogTestTarget,
    existing: AppTestTarget | null
  ): Target_change_summary => {
    if (!existing) {
      return {
        has_changes: true,
        change_labels: ["新規"],
        field_changes: [],
        params_changed: true,
        usecases_changed: false,
      };
    }

    const change_labels: string[] = [];
    const field_changes: Field_change[] = [];

    const name_changed = target.name !== existing.name;
    const type_changed =
      target.type?.toLowerCase() !== existing.type?.toLowerCase();
    const description_changed =
      normalize_empty_text(target.description) !==
      normalize_empty_text(existing.description);
    const parent_changed =
      target.parent_test_target_id !== getExistingParentId(existing);
    const params_changed = deepCompareParams(target.params, existing.params);

    if (name_changed) {
      change_labels.push("名前");
      field_changes.push({
        label: "名前",
        old_value: existing.name,
        new_value: target.name,
      });
    }
    if (type_changed) {
      change_labels.push("タイプ");
      field_changes.push({
        label: "タイプ",
        old_value: existing.type,
        new_value: target.type,
      });
    }
    if (description_changed) {
      change_labels.push("説明");
      field_changes.push({
        label: "説明",
        old_value: existing.description,
        new_value: target.description,
      });
    }
    if (parent_changed) {
      change_labels.push("親ID");
      field_changes.push({
        label: "親ID",
        old_value: getExistingParentId(existing),
        new_value: target.parent_test_target_id,
      });
    }
    if (params_changed) {
      change_labels.push("パラメータ");
    }

    let usecases_changed = false;
    if (target.type === "View") {
      const usecase_diff = analyzeUseCaseDiff(target);
      usecases_changed =
        usecase_diff.added.length > 0 ||
        usecase_diff.updated.length > 0 ||
        usecase_diff.removed.length > 0;
      if (usecases_changed) {
        change_labels.push("機能");
      }
    }

    return {
      has_changes: change_labels.length > 0,
      change_labels,
      field_changes,
      params_changed,
      usecases_changed,
    };
  };

  const get_param_mismatch_details = (
    new_params: unknown,
    existing_params: unknown
  ): {
    invalid_value_keys: string[];
    changed_keys: string[];
    serialization_error_keys: string[];
  } => {
    const invalid_value_keys: string[] = [];
    const changed_keys: string[] = [];
    const serialization_error_keys: string[] = [];

    const cleaned_new = excludeSystemFields(new_params || {}) as Record<
      string,
      unknown
    >;
    const cleaned_existing = excludeSystemFields(existing_params || {}) as Record<
      string,
      unknown
    >;
    const all_keys = new Set([
      ...Object.keys(cleaned_new),
      ...Object.keys(cleaned_existing),
    ]);

    const is_valid_value = (value: unknown): boolean => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    };

    all_keys.forEach((key) => {
      if (key === "usecases") return;
      const new_value = cleaned_new[key];
      const existing_value = cleaned_existing[key];
      const new_valid = is_valid_value(new_value);
      const existing_valid = is_valid_value(existing_value);

      if (new_valid !== existing_valid) {
        invalid_value_keys.push(key);
        return;
      }

      try {
        const new_str = JSON.stringify(new_value);
        const existing_str = JSON.stringify(existing_value);
        if (new_str !== existing_str) {
          changed_keys.push(key);
        }
      } catch {
        serialization_error_keys.push(key);
      }
    });

    return {
      invalid_value_keys,
      changed_keys,
      serialization_error_keys,
    };
  };

  // ユースケース情報を抽出する関数
  const extractUseCases = (target: OperationLogTestTarget) => {
    if (target.type !== "View" || !target.params?.usecases) return [];
    return target.params.usecases;
  };

  // ユースケースの操作タイプを取得（CREATE/UPDATE）
  const getUsecaseOperationType = (usecase: any): "CREATE" | "UPDATE" => {
    return usecase.operation_type === "UPDATE" ? "UPDATE" : "CREATE";
  };

  const SWITCH_FROM_CREATE_KEY = "_switchFromCreateId";

  const getPageViewId = (page: PageResult): string | undefined => {
    if (page.view_id) return page.view_id;
    const viewTarget = page.test_targets.find(
      (t) => t.type === "View" || t.type?.toLowerCase() === "view"
    );
    return viewTarget?.test_target_id || undefined;
  };

  const getExistingUsecasesForPage = (page: PageResult): any[] => {
    const viewId = getPageViewId(page);
    if (!viewId) return [];
    const existing = findExistingTarget(viewId);
    const usecases = (existing?.params as any)?.usecases;
    return Array.isArray(usecases) ? usecases : [];
  };

  /** CREATE 切替を反映したユースケース一覧（表示・登録用） */
  const getEffectiveUsecases = (page: PageResult): any[] => {
    if (!page.usecases || !Array.isArray(page.usecases)) return [];
    return page.usecases.map((uc: any) => {
      const sw = usecaseCreateSwitches.find(
        (s) =>
          s.groupIndex === page.group_index &&
          s.originalCreateId === uc.usecase_id
      );
      if (!sw) return uc;
      return {
        ...uc,
        operation_type: "UPDATE",
        usecase_id: sw.existingUsecaseId,
        [SWITCH_FROM_CREATE_KEY]: sw.originalCreateId,
      };
    });
  };

  const stripSwitchMeta = (uc: any) => {
    if (!uc || typeof uc !== "object") return uc;
    const { [SWITCH_FROM_CREATE_KEY]: _removed, ...rest } = uc;
    return rest;
  };

  const rankExistingUsecasesForSuggest = (
    createUc: any,
    existingList: any[]
  ): any[] => {
    const createName = String(createUc?.name || createUc?.title || "");
    return [...existingList].sort((a, b) => {
      const an = String(a?.name || a?.title || "");
      const bn = String(b?.name || b?.title || "");
      const aHit =
        an && createName && (an.includes(createName) || createName.includes(an))
          ? 0
          : 1;
      const bHit =
        bn && createName && (bn.includes(createName) || createName.includes(bn))
          ? 0
          : 1;
      if (aHit !== bHit) return aHit - bHit;
      return an.localeCompare(bn, "ja");
    });
  };

  const switchCreateUsecaseToExisting = (
    groupIndex: number,
    originalCreateId: string,
    existingUsecaseId: string
  ) => {
    const page = pageResults.find((p) => p.group_index === groupIndex);
    if (!page) return;

    const originalUc = (page.usecases || []).find(
      (uc: any) => uc.usecase_id === originalCreateId
    );
    if (!originalUc) return;

    const effectiveOthers = getEffectiveUsecases(page).filter(
      (uc: any) =>
        uc[SWITCH_FROM_CREATE_KEY] !== originalCreateId &&
        uc.usecase_id !== originalCreateId
    );
    if (effectiveOthers.some((uc: any) => uc.usecase_id === existingUsecaseId)) {
      setError(
        "選択した既存機能は、すでに別の生成結果で更新対象になっています。別の既存を選ぶか、先にそちらの選択を外してください。"
      );
      return;
    }

    setError(null);
    setUsecaseCreateSwitches((prev) => {
      const without = prev.filter(
        (s) =>
          !(
            s.groupIndex === groupIndex &&
            s.originalCreateId === originalCreateId
          )
      );
      return [
        ...without,
        {
          groupIndex,
          originalCreateId,
          originalUsecase: originalUc,
          existingUsecaseId,
        },
      ];
    });

    setSelectedUsecases((prev) => {
      const next = new Set(prev);
      next.delete(originalCreateId);
      const prevSwitch = usecaseCreateSwitches.find(
        (s) =>
          s.groupIndex === groupIndex && s.originalCreateId === originalCreateId
      );
      if (prevSwitch) next.delete(prevSwitch.existingUsecaseId);
      next.add(existingUsecaseId);
      return next;
    });
  };

  const revertCreateUsecaseSwitch = (
    groupIndex: number,
    originalCreateId: string
  ) => {
    const prevSwitch = usecaseCreateSwitches.find(
      (s) =>
        s.groupIndex === groupIndex && s.originalCreateId === originalCreateId
    );
    setUsecaseCreateSwitches((prev) =>
      prev.filter(
        (s) =>
          !(
            s.groupIndex === groupIndex &&
            s.originalCreateId === originalCreateId
          )
      )
    );
    setSelectedUsecases((prev) => {
      const next = new Set(prev);
      if (prevSwitch) next.delete(prevSwitch.existingUsecaseId);
      next.add(originalCreateId);
      return next;
    });
    setError(null);
  };

  // ユースケースの詳細差分を分析する関数
  const analyzeUseCaseFieldDiff = (newUseCase: any, existingUseCase: any) => {
    const fieldChanges: any = {};

    try {
      // existingUseCaseが存在しない場合の処理
      if (!existingUseCase) {
        return {};
      }

      // 基本フィールドの比較
      const basicFields = ["title", "name", "description"];
      basicFields.forEach((field) => {
        const newValue = newUseCase[field];
        const oldValue = existingUseCase[field];

        if (newValue !== oldValue) {
          fieldChanges[field] = {
            old: oldValue,
            new: newValue,
          };
        }
      });

      // stepsの比較（追加・更新のみ、削除は検出しない）
      const newSteps = newUseCase.steps || [];
      const existingSteps = existingUseCase.steps || [];
      if (JSON.stringify(newSteps) !== JSON.stringify(existingSteps)) {
        fieldChanges.steps = {
          old: existingSteps,
          new: newSteps,
          addedSteps: newSteps.filter(
            (ns: any) =>
              !existingSteps.find((es: any) => es.step_id === ns.step_id)
          ),
          modifiedSteps: newSteps.filter((ns: any) => {
            const existing = existingSteps.find(
              (es: any) => es.step_id === ns.step_id
            );
            return existing && JSON.stringify(ns) !== JSON.stringify(existing);
          }),
        };
      }

      // parametersの比較（追加・更新のみ、削除は検出しない）
      const newParams = newUseCase.parameters || [];
      const existingParams = existingUseCase.parameters || [];
      if (JSON.stringify(newParams) !== JSON.stringify(existingParams)) {
        fieldChanges.parameters = {
          old: existingParams,
          new: newParams,
          addedParams: newParams.filter(
            (np: any) => !existingParams.find((ep: any) => ep.name === np.name)
          ),
          modifiedParams: newParams.filter((np: any) => {
            const existing = existingParams.find(
              (ep: any) => ep.name === np.name
            );
            return existing && JSON.stringify(np) !== JSON.stringify(existing);
          }),
        };
      }

      return fieldChanges;
    } catch (error) {
      console.error("analyzeUseCaseFieldDiff - error:", error);
      return {};
    }
  };

  // ユースケースの差分を分析する関数（operation_typeベース）
  const analyzeUseCaseDiff = (target: OperationLogTestTarget) => {
    const existing = findExistingTarget(target.test_target_id || "");
    const newUseCases = extractUseCases(target);
    const existingUseCases = (existing?.params as any)?.usecases || [];

    // operation_typeを使って分類
    const added: any[] = [];
    const updated: any[] = [];

    newUseCases.forEach((newUc: any) => {
      const operationType = getUsecaseOperationType(newUc);

      if (operationType === "CREATE") {
        // 新規作成
        added.push(newUc);
      } else if (operationType === "UPDATE") {
        // 既存更新: 詳細差分を分析
        const existingUc = existingUseCases.find(
          (ex: any) => ex.usecase_id === newUc.usecase_id
        );

        updated.push({
          ...newUc,
          fieldChanges: analyzeUseCaseFieldDiff(newUc, existingUc),
        });
      }
    });

    // 削除されたユースケースは検出しない（追加・更新のみ）
    const removed: any[] = [];

    return { added, updated, removed, total: newUseCases.length };
  };

  // パラメータの差分を検出する関数（usecasesとシステム管理フィールドを除外）
  // 追加・更新のみを検出（削除・null化は検出しない）
  const getParameterDiff = (newParams: any, existingParams: any) => {
    if (!existingParams)
      return { added: newParams, removed: null, modified: null };

    const added: any = {};
    const modified: any = {};

    // 値が有効かチェックする関数（null, undefined, 空文字列を除外）
    const isValidValue = (value: any): boolean => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    };

    // システム管理フィールドとusecasesを除外して比較対象のキーを収集
    const allKeys = new Set([
      ...Object.keys(newParams || {}),
      ...Object.keys(existingParams || {}),
    ]);

    allKeys.forEach((key) => {
      // usecasesとシステム管理フィールドはスキップ
      if (key === "usecases" || SYSTEM_MANAGED_FIELDS.has(key)) {
        return;
      }

      const newValue = newParams?.[key];
      const existingValue = existingParams?.[key];

      // 新しい値がnullや空の場合はスキップ（null化は除外）
      if (!isValidValue(newValue) && !isValidValue(existingValue)) {
        return;
      }

      // 新規追加
      if (!(key in (existingParams || {})) && isValidValue(newValue)) {
        added[key] = newValue;
      }
      // 既存値の変更
      else if (
        key in (existingParams || {}) &&
        isValidValue(newValue) &&
        JSON.stringify(newValue) !== JSON.stringify(existingValue)
      ) {
        // transitions配列内のstatusフィールドを除外して比較
        if (key === "transitions" && Array.isArray(newValue) && Array.isArray(existingValue)) {
          const cleanedNew = newValue.map((t: any) => {
            const { status, ...rest } = t;
            return rest;
          });
          const cleanedExisting = existingValue.map((t: any) => {
            const { status, ...rest } = t;
            return rest;
          });
          if (JSON.stringify(cleanedNew) !== JSON.stringify(cleanedExisting)) {
            modified[key] = { old: existingValue, new: newValue };
          }
        } else {
          modified[key] = { old: existingValue, new: newValue };
        }
      }
    });

    // 削除されたパラメータは検出しない（マージ更新のため）
    return { added, removed: null, modified };
  };

  // 選択状態の統計
  const selectionStats = useMemo(() => {
    const selected = selectedTargets.filter((t) => t.selected).length;
    const total = selectedTargets.length;
    return { selected, total };
  }, [selectedTargets]);

  const registerSelectionParts = [
    selectionStats.selected > 0 ? `項目${selectionStats.selected}件` : "",
    selectedUsecases.size > 0 ? `機能${selectedUsecases.size}件` : "",
  ].filter(Boolean);
  const registerButtonLabel =
    registerSelectionParts.length > 0
      ? `選択した${registerSelectionParts.join("、")}を登録`
      : deleteCandidateIds.size > 0
        ? `削除${deleteCandidateIds.size}件を反映`
        : "登録対象なし";

  // page単位の統計情報
  const getPageStats = (groupIndex: number) => {
    const page = pageResults.find((p) => p.group_index === groupIndex);
    if (!page) return null;

    // 削除対象ページは test_targets（operation="DELETE"）をそのまま使う
    if (page.is_deletion_page) {
      return {
        total: page.test_targets.length,
        selected: 0,
        totalUseCases: 0,
        addedUseCases: 0,
        updatedUseCases: 0,
        isNewView: false,
      };
    }

    const pageTargetIds = new Set(
      page.test_targets.map((t) => t.test_target_id).filter((id) => id)
    );
    const pageTargets = selectedTargets.filter((t) =>
      pageTargetIds.has(t.test_target_id || "")
    );
    const selectedCount = pageTargets.filter((t) => t.selected).length;
    const changedTargetCount = pageTargets.filter((t) => {
      if (t.operation !== "UPDATE") return true;
      const existing = findExistingTarget(t.test_target_id || "");
      return get_target_change_summary(t, existing).has_changes;
    }).length;

    // ユースケース統計（CREATE→UPDATE 切替反映後）
    let totalUseCases = 0;
    let addedUseCases = 0;
    let updatedUseCases = 0;

    const effectiveUsecases = getEffectiveUsecases(page);
    totalUseCases = effectiveUsecases.length;
    effectiveUsecases.forEach((uc: any) => {
      const opType = getUsecaseOperationType(uc);
      if (opType === "CREATE") {
        addedUseCases++;
      } else if (opType === "UPDATE") {
        updatedUseCases++;
      }
    });

    return {
      total: pageTargets.length,
      selected: selectedCount,
      changedTargetCount,
      totalUseCases,
      addedUseCases,
      updatedUseCases,
      isNewView: page.is_new_view,
    };
  };

  // 選択されたページの情報を取得
  const selectedPage = useMemo(() => {
    if (selectedPageIndex === null) return null;
    return pageResults.find((p) => p.group_index === selectedPageIndex) || null;
  }, [selectedPageIndex, pageResults]);

  // 選択されたページのターゲット一覧を取得
  // 削除対象ページは test_targets（operation="DELETE"）をそのまま使う
  const selectedPageTargets = useMemo(() => {
    if (!selectedPage) return [];
    if (selectedPage.is_deletion_page) return selectedPage.test_targets;
    const pageTargetIds = new Set(
      selectedPage.test_targets.map((t) => t.test_target_id).filter((id) => id)
    );
    return selectedTargets.filter((t) =>
      pageTargetIds.has(t.test_target_id || "")
    );
  }, [selectedPage, selectedTargets]);


  // 選択されたページのユースケース統計
  const selectedPageUsecaseStats = useMemo(() => {
    if (!selectedPage) {
      return { total: 0, selected: 0 };
    }
    const effective = getEffectiveUsecases(selectedPage);
    const total = effective.length;
    const selected = effective.filter((uc: any) =>
      selectedUsecases.has(uc.usecase_id)
    ).length;
    return { total, selected };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPage, selectedUsecases, usecaseCreateSwitches]);

  // 全選択/全解除
  const handleSelectAll = (checked: boolean) => {
    setSelectedTargets((prev) =>
      prev.map((target) => ({ ...target, selected: checked }))
    );
  };

  // 個別選択（子をチェックしたら一時IDの親も自動選択、親を外したら子もまとめて外す）
  const handleTargetSelect = (targetId: string, checked: boolean) => {
    const existingIds = new Set(
      existingTargets.map((t) => t.id).filter((id): id is string => id != null)
    );
    const is_temp_parent = (parentId: string) =>
      parentId !== "" && !existingIds.has(parentId);

    setSelectedTargets((prev) => {
      if (checked) {
        // TASK: 子をチェックしたとき、一時IDの親を再帰的に選択（循環参照ガード付き）
        const visited = new Set<string>();
        const collect_parent_ids = (tid: string): string[] => {
          if (visited.has(tid)) return [];
          const target = prev.find((t) => t.test_target_id === tid);
          const parentId = target?.parent_test_target_id ?? null;
          if (parentId == null || parentId === "") return [];
          if (!is_temp_parent(parentId)) return [];
          const parent_in_prev = prev.some((t) => t.test_target_id === parentId);
          if (!parent_in_prev) return [];
          visited.add(tid);
          return [parentId, ...collect_parent_ids(parentId)];
        };
        const parent_ids_to_select = collect_parent_ids(targetId);
        const ids_to_select = new Set([
          targetId,
          ...parent_ids_to_select,
        ]);

        return prev.map((target) => {
          const id = target.test_target_id ?? "";
          return ids_to_select.has(id)
            ? { ...target, selected: true }
            : target;
        });
      }

      // 親のチェックを外したとき、その配下の子もまとめて外す
      const collect_descendant_ids = (tid: string): string[] => {
        const children = prev.filter((t) => t.parent_test_target_id === tid);
        const child_ids = children
          .map((t) => t.test_target_id)
          .filter((id): id is string => id != null && id !== "");
        return [
          ...child_ids,
          ...child_ids.flatMap((id) => collect_descendant_ids(id)),
        ];
      };
      const descendant_ids = collect_descendant_ids(targetId);
      const ids_to_unselect = new Set([targetId, ...descendant_ids]);

      return prev.map((target) => {
        const id = target.test_target_id ?? "";
        return ids_to_unselect.has(id)
          ? { ...target, selected: false }
          : target;
      });
    });
  };

  // 行の展開/折りたたみ
  const handleRowExpand = (targetId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(targetId)) {
        newSet.delete(targetId);
      } else {
        newSet.add(targetId);
      }
      return newSet;
    });
  };

  // ページ内の全ターゲットを選択/解除
  const handlePageSelectAll = (groupIndex: number, checked: boolean) => {
    const page = pageResults.find((p) => p.group_index === groupIndex);
    if (!page) return;

    const pageTargetIds = new Set(
      page.test_targets.map((t) => t.test_target_id).filter((id) => id)
    );

    setSelectedTargets((prev) =>
      prev.map((target) =>
        pageTargetIds.has(target.test_target_id || "")
          ? { ...target, selected: checked }
          : target
      )
    );
  };

  // ユースケースの個別選択
  const handleUsecaseSelect = (usecaseId: string, checked: boolean) => {
    setSelectedUsecases((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(usecaseId);
      } else {
        newSet.delete(usecaseId);
      }
      return newSet;
    });
  };

  // ページ内の全ユースケースを選択/解除
  const handlePageUsecaseSelectAll = (groupIndex: number, checked: boolean) => {
    const page = pageResults.find((p) => p.group_index === groupIndex);
    if (!page || !page.usecases) return;

    const effective = getEffectiveUsecases(page);
    setSelectedUsecases((prev) => {
      const newSet = new Set(prev);
      effective.forEach((uc: any) => {
        if (uc.usecase_id) {
          if (checked) {
            newSet.add(uc.usecase_id);
          } else {
            newSet.delete(uc.usecase_id);
          }
        }
      });
      return newSet;
    });
  };

  // 全ユースケースを選択/解除
  const handleAllUsecasesSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set<string>();
      pageResults.forEach((page) => {
        getEffectiveUsecases(page).forEach((uc: any) => {
          if (uc.usecase_id) {
            allIds.add(uc.usecase_id);
          }
        });
      });
      setSelectedUsecases(allIds);
    } else {
      setSelectedUsecases(new Set());
    }
  };

  // UPDATE操作時にパラメータをマージする関数
  const mergeParams = (newParams: any, existingParams: any): any => {
    if (!existingParams) return newParams;
    if (!newParams) return existingParams;

    // 値が有効かチェックする関数（null, undefined, 空文字列を除外）
    const isValidValue = (value: any): boolean => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    };

    // 既存パラメータをベースにコピー
    const merged = { ...existingParams };

    // 新規パラメータから有効な値のみをマージ
    Object.keys(newParams).forEach((key) => {
      const newValue = newParams[key];
      // 有効な値の場合のみマージ（null/空文字列は既存値を保持）
      if (isValidValue(newValue)) {
        merged[key] = newValue;
      }
    });

    return merged;
  };

  // テストターゲット登録処理
  const handleRegister = async () => {
    // 選択されたターゲットを取得
    const targetsToRegister = selectedTargets.filter((t) => t.selected);

    if (
      targetsToRegister.length === 0 &&
      selectedUsecases.size === 0 &&
      deleteCandidateIds.size === 0
    ) {
      setError("登録または削除する対象を選択してください");
      return;
    }

    if (!workspaceId || !projectId) {
      setError("ワークスペースIDとプロジェクトIDが必要です");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // アクセストークンを設定
      // Auth0認証を使用するため、userTokenは不要

      // UPDATE操作のターゲットについて、既存パラメータとマージ
      const processedTargets = targetsToRegister.map(
        ({ selected, ...target }) => {
          let processedTarget = { ...target };

          if (target.operation === "UPDATE" && target.test_target_id) {
            const existingTarget = findExistingTarget(target.test_target_id);
            if (existingTarget) {
              // パラメータをマージ（既存 + 新規/更新）
              const mergedParams = mergeParams(
                target.params,
                existingTarget.params
              );
              processedTarget = {
                ...target,
                params: mergedParams,
              };
            }
          }

          return processedTarget;
        }
      );

      const processedTargetsUnique =
        dedupe_targets_by_id_last_wins(processedTargets);

      // 選択されたユースケースをpageResults別に集約
      const usecaseUpdates: any[] = [];

      pageResults.forEach((page) => {
        const effectiveUsecases = getEffectiveUsecases(page);
        if (effectiveUsecases.length > 0) {
          // このページで選択されているユースケースをフィルタ
          const selectedPageUsecases = effectiveUsecases
            .filter((uc: any) => selectedUsecases.has(uc.usecase_id))
            .map(stripSwitchMeta);

          if (selectedPageUsecases.length > 0) {
            // View IDを取得（view_idフィールドがあればそれを使用、なければtest_targetsから探す）
            const viewId = getPageViewId(page) || page.test_targets.find((t) => t.type === "View")?.test_target_id;

            if (viewId) {
              // 操作ログ由来のユースケースにstatusが無い場合のデフォルトを設定
              // NOTE: 操作ログのインポートで新規作成の場合は"draft"をデフォルト値として設定
              const usecasesWithStatus = selectedPageUsecases.map((uc: any) => ({
                ...uc,
                status: uc.status || "draft",
              }));
              usecaseUpdates.push({
                view_test_target_id: viewId,
                usecases: usecasesWithStatus,
              });
              console.log(`  ✓ usecaseUpdateに追加: view_id=${viewId}, usecases=${selectedPageUsecases.length}件`);
            } else {
              console.warn(`  ⚠️ View IDが見つかりません`);
            }
          }
        }
      });

      const request: RegisterTestTargetsRequest = {
        testTargets: processedTargetsUnique,
        workspaceId,
        projectId,
        usecases: usecaseUpdates.length > 0 ? usecaseUpdates : undefined,
        traceId: traceId,
        traceparent: traceparent,
      };

      const response: RegisterTestTargetsResponse = customRegisterHandler
        ? await customRegisterHandler({
            ...request,
            deleteCandidateIds: deleteCandidateIds.size > 0 ? deleteCandidateIds : undefined,
            existingChildrenOf: existingChildrenOf,
          })
        : await operationLogService.registerTestTargets({
            ...request,
            // backend (/register → TestTargetCreateService) は operation を ADD/UPDATE/NONE で解釈するため、
            // 仕様書インポート由来の "CREATE" を "ADD" に正規化する (直 GraphQL 経路では operation を参照しない)。
            testTargets: request.testTargets.map((t) =>
              t.operation === "CREATE" ? { ...t, operation: "ADD" } : t
            ),
          });

      // 登録結果サマリを表示するため state に保存（閉じるで完了通知）
      setRegistrationResult(response);
    } catch (error: any) {
      setError(error.message || "テストターゲットの登録に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  /** 表示中の生成結果を GenerateFromOperationLogResponse 形式の JSON でダウンロード（再読み込みでダイアログ表示用） */
  const handleDownloadAsJson = () => {
    const payload: GenerateFromOperationLogResponse = {
      page_results: pageResults.map((page) => ({
        ...page,
        usecases: getEffectiveUsecases(page).map(stripSwitchMeta),
      })),
      message: "",
      trace_id: traceId,
      traceparent: traceparent,
      generation_note: generationNote,
      generation_note_link_url: generationNoteLinkUrl,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `test-target-generation-result-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ダイアログクローズ処理
  const handleClose = () => {
    setError(null);
    setLoading(false);
    setRegistrationResult(null);
    onClose();
  };

  // 登録結果サマリ表示後に閉じる（親に結果を通知してから閉じる）
  const handleCloseAfterSummary = () => {
    if (registrationResult) {
      onRegistrationComplete(registrationResult);
    }
    setRegistrationResult(null);
    setLoading(false);
    setError(null);
    onClose();
  };

  // タイプ別の色分け
  const getTypeColor = (type?: string) => {
    switch (type) {
      case "Service":
        return "primary";
      case "View":
        return "secondary";
      case "Section":
        return "success";
      case "Overlay":
        return "warning";
      case "Item":
        return "info";
      default:
        return "default";
    }
  };

  // 操作別の色分け
  const getOperationColor = (
    operation?: string
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    switch (operation) {
      case "ADD":
        return "success";
      case "UPDATE":
        return "warning";
      case "DELETE":
        return "error";
      case "NONE":
        return "default";
      default:
        return "default";
    }
  };

  // ユースケースの詳細変更内容を表示するコンポーネント
  const UseCaseDetailedDiff = ({ useCase }: { useCase: any }) => {
    const fieldChanges = useCase.fieldChanges || {};

    // 変更がない場合は何も表示しない
    if (Object.keys(fieldChanges).length === 0) {
      return (
        <Typography
          variant="caption"
          sx={{ display: "block", pl: 1, mt: 0.5, color: "text.secondary" }}
        >
          詳細な変更情報を取得中... (fieldChanges:{" "}
          {JSON.stringify(fieldChanges)})
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          mt: 1,
          pl: 1,
          borderLeft: "2px solid",
          borderLeftColor: "warning.main",
        }}
      >
        {/* 基本フィールドの変更 */}
        {Object.entries(fieldChanges).map(([field, change]: [string, any]) => {
          if (["title", "name", "description"].includes(field)) {
            return (
              <Box key={field} sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="warning.main"
                  sx={{ display: "block" }}
                >
                  📝{" "}
                  {field === "title"
                    ? "タイトル"
                    : field === "name"
                    ? "名前"
                    : "説明"}
                  の変更
                </Typography>
                <Box sx={{ pl: 1, py: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "error.main",
                      backgroundColor: "error.50",
                      p: 0.5,
                      borderRadius: 0.5,
                      mb: 0.5,
                    }}
                  >
                    ❌ 旧: {change.old || "(空)"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "success.main",
                      backgroundColor: "success.50",
                      p: 0.5,
                      borderRadius: 0.5,
                    }}
                  >
                    ✅ 新: {change.new || "(空)"}
                  </Typography>
                </Box>
              </Box>
            );
          }
          return null;
        })}

        {/* ステップの変更 */}
        {fieldChanges.steps && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              fontWeight="bold"
              color="warning.main"
              sx={{ display: "block" }}
            >
              🔧 ステップの変更
            </Typography>
            <Box sx={{ pl: 1 }}>
              {fieldChanges.steps.addedSteps?.length > 0 && (
                <Box sx={{ mb: 0.5 }}>
                  <Typography
                    variant="caption"
                    color="success.main"
                    fontWeight="bold"
                    sx={{ display: "block" }}
                  >
                    ➕ 追加されたステップ (
                    {fieldChanges.steps.addedSteps.length}件)
                  </Typography>
                  {fieldChanges.steps.addedSteps.map(
                    (step: any, index: number) => (
                      <Typography
                        key={index}
                        variant="caption"
                        sx={{
                          display: "block",
                          pl: 1,
                          py: 0.25,
                          backgroundColor: "success.50",
                          borderRadius: 0.5,
                          mb: 0.25,
                          fontSize: "0.7rem",
                        }}
                      >
                        • {step.step}
                      </Typography>
                    )
                  )}
                </Box>
              )}

              {fieldChanges.steps.modifiedSteps?.length > 0 && (
                <Box sx={{ mb: 0.5 }}>
                  <Typography
                    variant="caption"
                    color="warning.main"
                    fontWeight="bold"
                    sx={{ display: "block" }}
                  >
                    🔄 変更されたステップ (
                    {fieldChanges.steps.modifiedSteps.length}件)
                  </Typography>
                  {fieldChanges.steps.modifiedSteps.map(
                    (step: any, index: number) => (
                      <Typography
                        key={index}
                        variant="caption"
                        sx={{
                          display: "block",
                          pl: 1,
                          py: 0.25,
                          backgroundColor: "warning.50",
                          borderRadius: 0.5,
                          mb: 0.25,
                          fontSize: "0.7rem",
                        }}
                      >
                        • {step.step}
                      </Typography>
                    )
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* パラメータの変更 */}
        {fieldChanges.parameters && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              fontWeight="bold"
              color="warning.main"
              sx={{ display: "block" }}
            >
              ⚙️ パラメータの変更
            </Typography>
            <Box sx={{ pl: 1 }}>
              {fieldChanges.parameters.addedParams?.length > 0 && (
                <Box sx={{ mb: 0.5 }}>
                  <Typography
                    variant="caption"
                    color="success.main"
                    fontWeight="bold"
                    sx={{ display: "block" }}
                  >
                    ➕ 追加されたパラメータ (
                    {fieldChanges.parameters.addedParams.length}件)
                  </Typography>
                  {fieldChanges.parameters.addedParams.map(
                    (param: any, index: number) => (
                      <Typography
                        key={index}
                        variant="caption"
                        sx={{
                          display: "block",
                          pl: 1,
                          py: 0.25,
                          backgroundColor: "success.50",
                          borderRadius: 0.5,
                          mb: 0.25,
                          fontSize: "0.7rem",
                        }}
                      >
                        • {param.name}: {param.default_value}
                      </Typography>
                    )
                  )}
                </Box>
              )}

              {fieldChanges.parameters.modifiedParams?.length > 0 && (
                <Box sx={{ mb: 0.5 }}>
                  <Typography
                    variant="caption"
                    color="warning.main"
                    fontWeight="bold"
                    sx={{ display: "block" }}
                  >
                    🔄 変更されたパラメータ (
                    {fieldChanges.parameters.modifiedParams.length}件)
                  </Typography>
                  {fieldChanges.parameters.modifiedParams.map(
                    (param: any, index: number) => (
                      <Box key={index} sx={{ mb: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: "bold", display: "block" }}
                        >
                          {param.name}:
                        </Typography>
                        <Box sx={{ pl: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              py: 0.25,
                              backgroundColor: "error.50",
                              borderRadius: 0.5,
                              mb: 0.25,
                              fontSize: "0.7rem",
                            }}
                          >
                            ❌ 旧:{" "}
                            {param.old_value || param.default_value || "(未設定)"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              py: 0.25,
                              backgroundColor: "success.50",
                              borderRadius: 0.5,
                              fontSize: "0.7rem",
                            }}
                          >
                            ✅ 新: {param.default_value || "(未設定)"}
                          </Typography>
                        </Box>
                      </Box>
                    )
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* 変更がない場合の表示 */}
        {Object.keys(fieldChanges).length === 0 && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              fontStyle: "italic",
            }}
          >
            変更項目の詳細分析中...
          </Typography>
        )}
      </Box>
    );
  };

  // パラメータの表示用文字列生成
  const formatParams = (params: any) => {
    if (!params) return "";
    if (typeof params === "string") return params;
    return JSON.stringify(params, null, 2);
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: "85vh",
          maxHeight: "900px",
          maxWidth: "1100px",
        },
      }}
    >
      {registrationResult ? (
        <>
          <DialogTitle>登録結果</DialogTitle>
          <DialogContent sx={{ p: 2 }}>
            <Alert
              severity={
                (registrationResult.failed_items?.length ?? 0) > 0 ||
                (registrationResult.dropped_refs ?? 0) > 0
                  ? "warning"
                  : "success"
              }
              sx={{ mb: 2 }}
            >
              {registrationResult.message}
            </Alert>
            <Typography variant="subtitle2" gutterBottom>
              登録: {registrationResult.registered_count} 件
              {/* #884: 機能（旧ユースケース）はテストターゲットと別集計。機能だけ登録した
                  ケースで「登録: 0 件」だけになり結果が伝わらないため件数を併記する。 */}
              {(registrationResult.registered_usecases_count ?? 0) > 0 && (
                <> / 機能: {registrationResult.registered_usecases_count} 件</>
              )}
              {(registrationResult.deleted_count ?? 0) > 0 && (
                <> / 削除: {registrationResult.deleted_count} 件</>
              )}
              {(registrationResult.failed_items?.length ?? 0) > 0 && (
                <> / 失敗: {registrationResult.failed_items!.length} 件</>
              )}
            </Typography>
            {(registrationResult.failed_items?.length ?? 0) > 0 && (() => {
              const failedItems = registrationResult.failed_items!;
              const targetItems = failedItems.filter(
                (item) => item.item_type === "target" || item.item_type == null
              );
              const usecaseItems = failedItems.filter(
                (item) => item.item_type === "usecase"
              );
              return (
                <Box>
                  {targetItems.length > 0 && (
                    <>
                      <Typography variant="subtitle2" color="error" gutterBottom sx={{ mt: 1 }}>
                        失敗したターゲット:
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                        <Table size="small" aria-label="失敗したターゲット一覧">
                          <TableHead>
                            <TableRow>
                              <TableCell variant="head" sx={{ fontWeight: 600 }}>
                                ターゲット名
                              </TableCell>
                              <TableCell variant="head" sx={{ fontWeight: 600 }}>
                                登録先View
                              </TableCell>
                              <TableCell variant="head" sx={{ fontWeight: 600 }}>
                                理由
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {targetItems.map((item, idx) => (
                              <TableRow key={`target-${idx}`}>
                                <TableCell>{item.target_name ?? "—"}</TableCell>
                                <TableCell>{item.view_name ?? "—"}</TableCell>
                                <TableCell>{item.reason}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                  {usecaseItems.length > 0 && (
                    <>
                      <Typography variant="subtitle2" color="error" gutterBottom sx={{ mt: targetItems.length > 0 ? 2 : 1 }}>
                        失敗した機能:
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                        <Table size="small" aria-label="失敗した機能一覧">
                          <TableHead>
                            <TableRow>
                              <TableCell variant="head" sx={{ fontWeight: 600 }}>
                                機能名
                              </TableCell>
                              <TableCell variant="head" sx={{ fontWeight: 600 }}>
                                登録先View
                              </TableCell>
                              <TableCell variant="head" sx={{ fontWeight: 600 }}>
                                理由
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {usecaseItems.map((item, idx) => (
                              <TableRow key={`usecase-${idx}`}>
                                <TableCell>{item.target_name ?? "—"}</TableCell>
                                <TableCell>{item.view_name ?? "—"}</TableCell>
                                <TableCell>{item.reason}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAfterSummary} variant="contained">
              閉じる
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Save />
            テストターゲット登録
            {isGenerationResultExportImportEnabled() && (
              <Tooltip title="表示中の生成結果をJSONでダウンロード（再読み込みでダイアログ表示に使用）" arrow>
                <IconButton
                  size="small"
                  onClick={handleDownloadAsJson}
                  aria-label="JSONでダウンロード"
                >
                  <Download />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            gap={0.5}
          >
            {selectedTargets.length === 0 && selectedUsecases.size === 0 ? (
              <Typography
                variant="body2"
                color="warning.main"
                fontWeight="medium"
              >
                ⚠️ 更新対象なし
              </Typography>
            ) : (
              <>
                {selectedTargets.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    項目: {selectionStats.selected} / {selectionStats.total}{" "}
                    件選択
                  </Typography>
                )}
                {selectedUsecases.size > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    機能: {selectedUsecases.size} 件選択
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: "calc(85vh - 140px)" }}>
        {/* 生成時の補足（トークン制限でpages省略した旨など） */}
        {generationNote && (
          <Box p={2} pb={0}>
            <Alert severity="info" sx={{ mb: 0 }}>
              {generationNoteLinkUrl ? (() => {
                const parts = generationNote.split("{{MANUAL_LINK}}");
                return (
                  <>
                    {parts.map((part, i) => (
                      <React.Fragment key={i}>
                        {part}
                        {i < parts.length - 1 && (
                          <Link
                            href={generationNoteLinkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontWeight: 600 }}
                          >
                            こちら
                          </Link>
                        )}
                      </React.Fragment>
                    ))}
                  </>
                );
              })() : (
                generationNote
              )}
            </Alert>
          </Box>
        )}
        {/* エラー表示 */}
        {error && (
          <Box p={2}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}

        {!hasRegistrableContent(filteredPageResults) ? (
          // test_targetsもusecasesもない場合の表示
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="300px"
            p={4}
            sx={{
              backgroundColor: "grey.50",
              borderRadius: 1,
              border: "2px dashed",
              borderColor: "grey.300",
            }}
          >
            <Box
              sx={{
                fontSize: "4rem",
                color: "grey.400",
                mb: 2,
              }}
            >
              ⚠️
            </Box>
            <Typography
              variant="h6"
              color="text.secondary"
              gutterBottom
              sx={{ fontWeight: "medium" }}
            >
              更新対象のターゲットがありません
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ maxWidth: "400px", lineHeight: 1.6 }}
            >
              インポートされたデータは既存データと同じです。
              <br />
              新しいテストターゲットを登録するには、別のデータをインポートしてください。
            </Typography>
            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: "info.50",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "info.200",
                maxWidth: "500px",
              }}
            >
              <Typography
                variant="caption"
                color="info.700"
                sx={{ fontWeight: "medium", display: "block", mb: 1 }}
              >
                💡 ヒント
              </Typography>
              <Typography
                variant="caption"
                color="info.600"
                sx={{ display: "block", lineHeight: 1.5 }}
              >
                • 操作ログファイルに新しい操作が含まれているか確認してください
                <br />
                • 異なるテストシナリオのファイルを試してください
                <br />• ファイルの形式が正しいか確認してください
              </Typography>
            </Box>
          </Box>
        ) : (
          /* 2カラムレイアウト */
          <Box display="flex" height="100%">
            {/* 左カラム: 画面一覧 */}
            <Box
              sx={{
                width: "240px",
                borderRight: "1px solid",
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* 全選択チェックボックス */}
              <Box p={2} borderBottom={1} borderColor="divider">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        selectionStats.selected === selectionStats.total &&
                        selectionStats.total > 0
                      }
                      indeterminate={
                        selectionStats.selected > 0 &&
                        selectionStats.selected < selectionStats.total
                      }
                      onChange={(e) => {
                        handleSelectAll(e.target.checked);
                        handleAllUsecasesSelectAll(e.target.checked);
                      }}
                    />
                  }
                  label={`全選択 (${selectionStats.selected}/${selectionStats.total})`}
                />
              </Box>

              {/* 画面一覧 */}
              <List sx={{ flex: 1, overflow: "auto", p: 0 }}>
                {pageResults.map((page) => {
                  const pageStats = getPageStats(page.group_index);
                  const isSelected = selectedPageIndex === page.group_index;

                  return (
                    <React.Fragment key={page.group_index}>
                      <ListItem disablePadding>
                        <ListItemButton
                          selected={isSelected}
                          onClick={() => setSelectedPageIndex(page.group_index)}
                          sx={{
                            borderLeft: isSelected
                              ? "4px solid"
                              : "4px solid transparent",
                            borderLeftColor: "primary.main",
                            backgroundColor: page.is_deletion_page
                              ? "error.50"
                              : page.is_new_view
                              ? "success.50"
                              : isSelected
                              ? "action.selected"
                              : "inherit",
                            "&:hover": {
                              backgroundColor: page.is_deletion_page
                                ? "error.100"
                                : page.is_new_view
                                ? "success.100"
                                : "action.hover",
                            },
                          }}
                        >
                          <Box sx={{ width: "100%", py: 1 }}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={0.5}
                            >
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                noWrap
                                sx={{ flex: 1 }}
                              >
                                {page.path}
                              </Typography>
                              {page.is_deletion_page ? (() => {
                                // 削除対象ページ: deleteCandidateIds に基づいてチェック状態を計算
                                const pageIds = page.test_targets
                                  .map((t) => t.test_target_id)
                                  .filter((id): id is string => id != null && id !== "");
                                const checkedCount = pageIds.filter((id) => deleteCandidateIds.has(id)).length;
                                const allChecked = pageIds.length > 0 && checkedCount === pageIds.length;
                                const someChecked = checkedCount > 0 && checkedCount < pageIds.length;
                                return (
                                  <Checkbox
                                    size="small"
                                    checked={allChecked}
                                    indeterminate={someChecked}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      setDeleteCandidateIds((prev) => {
                                        const next = new Set(prev);
                                        if (e.target.checked) {
                                          pageIds.forEach((id) => next.add(id));
                                        } else {
                                          pageIds.forEach((id) => next.delete(id));
                                        }
                                        return next;
                                      });
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ color: "error.main" }}
                                  />
                                );
                              })() : (
                                <Checkbox
                                  size="small"
                                  checked={
                                    pageStats?.selected === pageStats?.total &&
                                    (pageStats?.total || 0) > 0
                                  }
                                  indeterminate={
                                    (pageStats?.selected || 0) > 0 &&
                                    pageStats?.selected !== pageStats?.total
                                  }
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handlePageSelectAll(
                                      page.group_index,
                                      e.target.checked
                                    );
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              )}
                            </Box>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {page.is_deletion_page ? (
                              <Chip
                                label="DELETE"
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ height: "20px", fontSize: "0.7rem" }}
                              />
                            ) : (
                              <Chip
                                label={
                                  viewMode === "hierarchical"
                                    ? page.is_new_view
                                      ? "CREATE"
                                      : (pageStats?.changedTargetCount ?? 0) > 0
                                        ? "UPDATE"
                                        : "変更なし"
                                    : page.is_new_view
                                      ? "新規"
                                      : "更新"
                                }
                                size="small"
                                color={
                                  page.is_new_view
                                    ? "success"
                                    : viewMode === "hierarchical" &&
                                      (pageStats?.changedTargetCount ?? 0) === 0
                                      ? "default"
                                      : "warning"
                                }
                                variant="outlined"
                                sx={{ height: "20px", fontSize: "0.7rem" }}
                              />
                            )}
                            <Chip
                              label={`項目: ${pageStats?.total || 0}`}
                              size="small"
                              variant="outlined"
                              sx={{ height: "20px", fontSize: "0.7rem" }}
                            />
                            {pageStats &&
                              (pageStats.addedUseCases > 0 ||
                                pageStats.updatedUseCases > 0) && (
                                <Chip
                                  label={`機能: ${
                                    (pageStats.addedUseCases || 0) +
                                    (pageStats.updatedUseCases || 0)
                                  }`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: "20px", fontSize: "0.7rem" }}
                                />
                              )}
                            </Box>
                          </Box>
                        </ListItemButton>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  );
                })}
              </List>
            </Box>

            {/* 右カラム: 詳細情報 */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              {selectedPage ? (
                <>
                  {/* タブヘッダー
                      hierarchical (= 仕様書インポート) では「項目」しかなく、SpecRegistrationHierarchicalView 内に
                      「項目」見出しのツールバーがあるためタブヘッダー自体を非表示。
                      操作ログ / JSON インポートは従来通りタブを表示。 */}
                  {viewMode !== "hierarchical" && (
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <Tabs
                        value={currentTab}
                        onChange={(e, v) => setCurrentTab(v)}
                      >
                        <Tab
                          icon={<Article />}
                          label="項目"
                          iconPosition="start"
                          sx={{ minHeight: "48px" }}
                        />
                        {!hideUsecasesTab && (
                          <Tab
                            icon={<PlaylistAddCheck />}
                            label="機能"
                            iconPosition="start"
                            sx={{ minHeight: "48px" }}
                          />
                        )}
                      </Tabs>
                    </Box>
                  )}

                  {/* タブコンテンツ */}
                  <Box sx={{ flex: 1, overflow: "auto", p: viewMode === "hierarchical" ? 0 : 2 }}>
                    {currentTab === 0 ? (
                      /* 項目タブ */
                      viewMode === "hierarchical" ? (
                        <SpecRegistrationHierarchicalView
                          selectedPage={selectedPage}
                          selectedPageTargets={selectedPageTargets}
                          selectedTargets={selectedTargets}
                          setSelectedTargets={setSelectedTargets}
                          findExistingTarget={findExistingTarget}
                          get_target_change_summary={get_target_change_summary}
                          deleteCandidateIds={deleteCandidateIds}
                          setDeleteCandidateIds={setDeleteCandidateIds}
                          isDeletionPage={selectedPage?.is_deletion_page}
                        />
                      ) : (
                      <Box>
                        <TableContainer component={Paper} elevation={0}>
                          <Table
                            size="small"
                            sx={{
                              tableLayout: "fixed",
                              width: "100%",
                            }}
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  padding="checkbox"
                                  sx={{ width: "50px" }}
                                >
                                  選択
                                </TableCell>
                                <TableCell sx={{ width: "80px" }}>
                                  操作
                                </TableCell>
                                <TableCell sx={{ width: "150px" }}>
                                  名前
                                </TableCell>
                                <TableCell sx={{ width: "80px" }}>
                                  タイプ
                                </TableCell>
                                <TableCell sx={{ width: "60px" }}>
                                  詳細
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedPageTargets.map((target) => (
                                <React.Fragment
                                  key={target.test_target_id || Math.random()}
                                >
                                  <TableRow hover>
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        checked={target.selected || false}
                                        onChange={(e) =>
                                          handleTargetSelect(
                                            target.test_target_id || "",
                                            e.target.checked
                                          )
                                        }
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={0.5}
                                      >
                                        <Chip
                                          label={target.operation || "NONE"}
                                          variant="outlined"
                                          size="small"
                                          color={getOperationColor(
                                            target.operation
                                          )}
                                        />
                                        {target.operation === "UPDATE" && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {(() => {
                                              const existing =
                                                findExistingTarget(
                                                  target.test_target_id || ""
                                                );
                                              if (!existing) return "新規作成";
                                              const summary =
                                                get_target_change_summary(
                                                  target,
                                                  existing
                                                );
                                              return summary.has_changes
                                                ? `変更: ${summary.change_labels.join(" / ")}`
                                                : "変更なし";
                                            })()}
                                          </Typography>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip
                                        title={
                                          target.name ||
                                          target.test_target_id ||
                                          "無名"
                                        }
                                        arrow
                                      >
                                        <Typography
                                          variant="body2"
                                          fontWeight="medium"
                                          sx={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            maxWidth: "150px",
                                            cursor: "help",
                                          }}
                                        >
                                          {target.name ||
                                            target.test_target_id ||
                                            "無名"}
                                        </Typography>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={target.type || "Unknown"}
                                        color={getTypeColor(target.type) as any}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip title="詳細を表示">
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleRowExpand(
                                              target.test_target_id || ""
                                            )
                                          }
                                        >
                                          {expandedRows.has(
                                            target.test_target_id || ""
                                          ) ? (
                                            <ExpandLess />
                                          ) : (
                                            <ExpandMore />
                                          )}
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>

                                  {/* 展開行 */}
                                  <TableRow>
                                    <TableCell
                                      colSpan={5}
                                      sx={{
                                        p: 0,
                                        maxWidth: "800px",
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Collapse
                                        in={expandedRows.has(
                                          target.test_target_id || ""
                                        )}
                                      >
                                        <Box p={2} bgcolor="grey.50">
                                          <Typography
                                            variant="subtitle2"
                                            gutterBottom
                                          >
                                            詳細情報
                                          </Typography>
                                          <Box>
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              <strong>ターゲットID:</strong>{" "}
                                              {target.test_target_id || "-"}
                                              {target.operation === "ADD" && (
                                                <Typography
                                                  variant="caption"
                                                  component="span"
                                                  sx={{ ml: 1 }}
                                                >
                                                  (仮ID)
                                                </Typography>
                                              )}
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              <strong>親ターゲット:</strong>{" "}
                                              {get_parent_display_label(
                                                target.parent_test_target_id
                                              )}
                                            </Typography>
                                          </Box>
                                          <Box
                                            display="grid"
                                            gridTemplateColumns="1fr"
                                            gap={2}
                                            maxWidth="800px"
                                          >
                                            {(target.operation === "UPDATE" ||
                                              target.operation === "CREATE" ||
                                              target.operation === "ADD") && (
                                              <Box sx={{ mt: 2 }}>
                                                {(() => {
                                                  if (
                                                    target.operation === "CREATE" ||
                                                    target.operation === "ADD"
                                                  ) {
                                                    return (
                                                      <Box>
                                                        <Typography
                                                          variant="body2"
                                                          color="text.secondary"
                                                          sx={{ mb: 1 }}
                                                        >
                                                          <strong>
                                                            変更内容:
                                                          </strong>{" "}
                                                        </Typography>
                                                        <Box
                                                          component="pre"
                                                          sx={{
                                                            ...diff_box_base_sx,
                                                            ...diff_box_new_sx,
                                                          }}
                                                        >
                                                          {get_description_text(
                                                            target
                                                          )}
                                                        </Box>
                                                      </Box>
                                                    );
                                                  }

                                                  const existing =
                                                    findExistingTarget(
                                                      target.test_target_id ||
                                                        ""
                                                    );
                                                  if (!existing) return null;
                                                  const summary =
                                                    get_target_change_summary(
                                                      target,
                                                      existing
                                                    );
                                                  if (!summary.has_changes) {
                                                    return (
                                                      <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: "block" }}
                                                      >
                                                        変更はありません
                                                      </Typography>
                                                    );
                                                  }

                                                  return (
                                                    <Box>
                                                      <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mb: 1 }}
                                                      >
                                                        <strong>
                                                          変更内容:
                                                        </strong>{" "}
                                                        {summary.change_labels.join(
                                                          " / "
                                                        )}
                                                      </Typography>
                                                      {summary.field_changes.map(
                                                        (change) => (
                                                          <Box
                                                            key={change.label}
                                                            sx={{ mb: 1 }}
                                                          >
                                                            <Typography
                                                              variant="caption"
                                                              fontWeight="bold"
                                                              sx={{
                                                                display: "block",
                                                                color: "text.secondary",
                                                              }}
                                                            >
                                                              {change.label}
                                                            </Typography>
                                                            <Box sx={{ pl: 1, py: 0.5 }}>
                                                              {render_diff_box(
                                                                "旧",
                                                                format_field_value(
                                                                  change.old_value
                                                                ),
                                                                "old",
                                                                false
                                                              )}
                                                              {render_diff_box(
                                                                "新",
                                                                format_field_value(
                                                                  change.new_value
                                                                ),
                                                                "new",
                                                                true
                                                              )}
                                                            </Box>
                                                          </Box>
                                                        )
                                                      )}
                                                    </Box>
                                                  );
                                                })()}
                                              </Box>
                                            )}

                                            {target.type === "View" && (
                                              <Box>
                                                <Typography
                                                  variant="body2"
                                                  color="text.secondary"
                                                  sx={{ mb: 1 }}
                                                >
                                                  <strong>
                                                    機能詳細:
                                                  </strong>
                                                </Typography>
                                                {(() => {
                                                  const useCaseDiff =
                                                    analyzeUseCaseDiff(target);
                                                  const useCases =
                                                    extractUseCases(target);

                                                  if (useCases.length === 0) {
                                                    return (
                                                      <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ pl: 2 }}
                                                      >
                                                        機能がありません
                                                      </Typography>
                                                    );
                                                  }

                                                  return (
                                                    <Box
                                                      sx={{
                                                        maxHeight: "200px",
                                                        overflow: "auto",
                                                      }}
                                                    >
                                                      {useCaseDiff.added
                                                        .length > 0 && (
                                                        <Box sx={{ mb: 1 }}>
                                                          <Typography
                                                            variant="caption"
                                                            color="success.main"
                                                            fontWeight="bold"
                                                          >
                                                            ➕
                                                            新規追加された機能:
                                                          </Typography>
                                                          {useCaseDiff.added.map(
                                                            (
                                                              uc: any,
                                                              index: number
                                                            ) => (
                                                              <Box
                                                                key={`added-${index}`}
                                                                sx={{
                                                                  pl: 2,
                                                                  py: 0.5,
                                                                  backgroundColor:
                                                                    "success.50",
                                                                  borderRadius: 1,
                                                                  mb: 0.5,
                                                                }}
                                                              >
                                                                <Typography
                                                                  variant="caption"
                                                                  fontWeight="bold"
                                                                >
                                                                  {uc.title ||
                                                                    uc.name}
                                                                </Typography>
                                                                <Typography
                                                                  variant="caption"
                                                                  sx={{
                                                                    display:
                                                                      "block",
                                                                    color:
                                                                      "text.secondary",
                                                                  }}
                                                                >
                                                                  {
                                                                    uc.description
                                                                  }
                                                                </Typography>
                                                              </Box>
                                                            )
                                                          )}
                                                        </Box>
                                                      )}

                                                      {useCaseDiff.updated
                                                        .length > 0 && (
                                                        <Box sx={{ mb: 1 }}>
                                                          <Typography
                                                            variant="caption"
                                                            color="warning.main"
                                                            fontWeight="bold"
                                                          >
                                                            🔄
                                                            更新された機能:
                                                          </Typography>
                                                          {useCaseDiff.updated.map(
                                                            (
                                                              uc: any,
                                                              index: number
                                                            ) => (
                                                              <Box
                                                                key={`updated-${index}`}
                                                                sx={{
                                                                  pl: 2,
                                                                  py: 0.5,
                                                                  backgroundColor:
                                                                    "warning.50",
                                                                  borderRadius: 1,
                                                                  mb: 0.5,
                                                                }}
                                                              >
                                                                <Typography
                                                                  variant="caption"
                                                                  fontWeight="bold"
                                                                >
                                                                  {uc.title ||
                                                                    uc.name}
                                                                </Typography>
                                                                <Typography
                                                                  variant="caption"
                                                                  sx={{
                                                                    display:
                                                                      "block",
                                                                    color:
                                                                      "text.secondary",
                                                                  }}
                                                                >
                                                                  {
                                                                    uc.description
                                                                  }
                                                                </Typography>
                                                                {/* 詳細差分表示 */}
                                                                <UseCaseDetailedDiff
                                                                  useCase={uc}
                                                                />
                                                              </Box>
                                                            )
                                                          )}
                                                        </Box>
                                                      )}

                                                      {useCaseDiff.removed
                                                        .length > 0 && (
                                                        <Box sx={{ mb: 1 }}>
                                                          <Typography
                                                            variant="caption"
                                                            color="error.main"
                                                            fontWeight="bold"
                                                          >
                                                            ➖
                                                            削除された機能:
                                                          </Typography>
                                                          {useCaseDiff.removed.map(
                                                            (
                                                              uc: any,
                                                              index: number
                                                            ) => (
                                                              <Box
                                                                key={`removed-${index}`}
                                                                sx={{
                                                                  pl: 2,
                                                                  py: 0.5,
                                                                  backgroundColor:
                                                                    "error.50",
                                                                  borderRadius: 1,
                                                                  mb: 0.5,
                                                                }}
                                                              >
                                                                <Typography
                                                                  variant="caption"
                                                                  fontWeight="bold"
                                                                >
                                                                  {uc.title ||
                                                                    uc.name}
                                                                </Typography>
                                                                <Typography
                                                                  variant="caption"
                                                                  sx={{
                                                                    display:
                                                                      "block",
                                                                    color:
                                                                      "text.secondary",
                                                                  }}
                                                                >
                                                                  {
                                                                    uc.description
                                                                  }
                                                                </Typography>
                                                              </Box>
                                                            )
                                                          )}
                                                        </Box>
                                                      )}

                                                      {useCaseDiff.added
                                                        .length === 0 &&
                                                        useCaseDiff.updated
                                                          .length === 0 &&
                                                        useCaseDiff.removed
                                                          .length === 0 && (
                                                          <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ pl: 2 }}
                                                          >
                                                            機能に変更はありません
                                                          </Typography>
                                                        )}
                                                    </Box>
                                                  );
                                                })()}
                                              </Box>
                                            )}

                                            <Box>
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                              >
                                                <strong>パラメータ:</strong>
                                                {target.type === "View" && (
                                                  <Typography
                                                    variant="caption"
                                                    component="span"
                                                    sx={{
                                                      ml: 1,
                                                      fontStyle: "italic",
                                                    }}
                                                  >
                                                    (機能は別タブに表示)
                                                  </Typography>
                                                )}
                                                {target.operation ===
                                                  "UPDATE" && (
                                                  <Typography
                                                    variant="caption"
                                                    component="span"
                                                    sx={{ ml: 1 }}
                                                  >
                                                    {(() => {
                                                      const existing =
                                                        findExistingTarget(
                                                          target.test_target_id ||
                                                            ""
                                                        );
                                                      if (!existing) {
                                                        return "(新規)";
                                                      }
                                                      const diff =
                                                        getParameterDiff(
                                                          target.params,
                                                          existing.params
                                                        );
                                                      const hasAdded =
                                                        Object.keys(
                                                          diff.added || {}
                                                        ).length > 0;
                                                      const hasModified =
                                                        Object.keys(
                                                          diff.modified || {}
                                                        ).length > 0;
                                                      return hasAdded ||
                                                        hasModified
                                                        ? "(差分表示)"
                                                        : "(差分なし)";
                                                    })()}
                                                  </Typography>
                                                )}
                                              </Typography>
                                              {target.operation === "UPDATE" ? (
                                                (() => {
                                                  const existing =
                                                    findExistingTarget(
                                                      target.test_target_id ||
                                                        ""
                                                    );
                                                  if (!existing) {
                                                    return (
                                                      <Box
                                                        component="pre"
                                                        sx={{
                                                          ...diff_box_base_sx,
                                                          backgroundColor:
                                                            "green.50",
                                                          borderColor:
                                                            "success.main",
                                                        }}
                                                      >
                                                        📄 新規作成{"\n"}
                                                        {formatParams(
                                                          target.params
                                                        ) || "なし"}
                                                      </Box>
                                                    );
                                                  }

                                                  const diff = getParameterDiff(
                                                    target.params,
                                                    existing.params
                                                  );

                                                  const hasAdded = Object.keys(diff.added || {}).length > 0;
                                                  const hasModified = Object.keys(diff.modified || {}).length > 0;
                                                  const hasDiff = hasAdded || hasModified;
                                                  const paramsChanged = deepCompareParams(
                                                    target.params,
                                                    existing.params
                                                  );
                                                  const mismatchKey = target.test_target_id || "";
                                                  if (
                                                    paramsChanged &&
                                                    !hasDiff &&
                                                    mismatchKey &&
                                                    !logged_param_mismatch.current.has(mismatchKey)
                                                  ) {
                                                    logged_param_mismatch.current.add(mismatchKey);
                                                    const newUsecases = (target.params as any)?.usecases;
                                                    const existingUsecases = (existing.params as any)?.usecases;
                                                    const usecasesChanged =
                                                      JSON.stringify(newUsecases || null) !==
                                                      JSON.stringify(existingUsecases || null);
                                                    const mismatch_details =
                                                      get_param_mismatch_details(
                                                        target.params,
                                                        existing.params
                                                      );
                                                    console.warn(
                                                      "PARAM_DIFF_MISMATCH",
                                                      {
                                                        targetId: mismatchKey,
                                                        operation: target.operation,
                                                        paramsChanged,
                                                        hasDiff,
                                                        usecasesChanged,
                                                        newParamKeys: Object.keys(target.params || {}),
                                                        existingParamKeys: Object.keys(
                                                          existing.params || {}
                                                        ),
                                                        mismatchDetails: mismatch_details,
                                                      }
                                                    );
                                                  }

                                                  return (
                                                    <Box>
                                                      {!hasDiff && (
                                                        <Typography
                                                          variant="caption"
                                                          color="text.secondary"
                                                          sx={{ display: "block", mb: 1 }}
                                                        >
                                                          パラメータに変更はありません
                                                        </Typography>
                                                      )}
                                                      {hasAdded && (
                                                        <Box sx={{ mb: 1 }}>
                                                          <Typography
                                                            variant="caption"
                                                            color="success.main"
                                                            fontWeight="bold"
                                                          >
                                                            ➕
                                                            追加されたパラメータ:
                                                          </Typography>
                                                          <Box
                                                            component="pre"
                                                            sx={{
                                                            ...diff_box_base_sx,
                                                            ...diff_box_new_sx,
                                                            }}
                                                          >
                                                          {format_json_value(
                                                            diff.added
                                                          )}
                                                          </Box>
                                                        </Box>
                                                      )}

                                                      {hasModified && (
                                                        <Box sx={{ mb: 1 }}>
                                                          {Object.entries(
                                                            diff.modified
                                                          ).map(
                                                            ([key, value]: [
                                                              string,
                                                              any
                                                            ]) => (
                                                              <Box
                                                                key={key}
                                                                sx={{ mb: 1 }}
                                                              >
                                                                <Typography
                                                                  variant="caption"
                                                                  sx={{
                                                                    fontWeight:
                                                                      "bold",
                                                                  }}
                                                                >
                                                                  {key}:
                                                                </Typography>
                                                                <Box sx={{ pl: 1 }}>
                                                                  {render_diff_box(
                                                                    "旧",
                                                                    format_json_value(
                                                                      value.old
                                                                    ),
                                                                    "old",
                                                                    false
                                                                  )}
                                                                  {render_diff_box(
                                                                    "新",
                                                                    format_json_value(
                                                                      value.new
                                                                    ),
                                                                    "new",
                                                                    true
                                                                  )}
                                                                </Box>
                                                              </Box>
                                                            )
                                                          )}
                                                        </Box>
                                                      )}

                                                      {/* 変更がないターゲットは既にフィルタリングされているため、この表示は不要 */}
                                                    </Box>
                                                  );
                                                })()
                                              ) : (
                                                <Box
                                                  component="pre"
                                                  sx={{
                                                    ...diff_box_base_sx,
                                                    backgroundColor: "grey.100",
                                                    borderColor: "grey.300",
                                                  }}
                                                >
                                                  {formatParams(
                                                    target.params
                                                  ) || "なし"}
                                                </Box>
                                              )}
                                            </Box>
                                          </Box>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                      )
                    ) : (
                      /* ユースケースタブ */
                      <Box>
                        {selectedPage.usecases &&
                        selectedPage.usecases.length > 0 ? (
                          <Box>
                            {/* UPDATE操作がある場合の情報メッセージ */}
                            {selectedPage.test_targets.some(
                              (t: any) =>
                                t.type === "View" && t.operation === "UPDATE"
                            ) && (
                              <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                  既存の画面を更新する場合、選択されていない機能は既存のまま保持されます。
                                </Typography>
                              </Alert>
                            )}

                            {/* 全選択チェックボックス */}
                            <Box
                              sx={{
                                mb: 2,
                                pb: 1,
                                borderBottom: 1,
                                borderColor: "divider",
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={
                                      selectedPageUsecaseStats.selected ===
                                        selectedPageUsecaseStats.total &&
                                      selectedPageUsecaseStats.total > 0
                                    }
                                    indeterminate={
                                      selectedPageUsecaseStats.selected > 0 &&
                                      selectedPageUsecaseStats.selected <
                                        selectedPageUsecaseStats.total
                                    }
                                    onChange={(e) =>
                                      handlePageUsecaseSelectAll(
                                        selectedPage.group_index,
                                        e.target.checked
                                      )
                                    }
                                  />
                                }
                                label={`全選択 (${selectedPageUsecaseStats.selected}/${selectedPageUsecaseStats.total})`}
                              />
                            </Box>

                            {/* ユースケース一覧 */}
                            <Box display="flex" flexDirection="column" gap={2}>
                              {getEffectiveUsecases(selectedPage).map(
                                (usecase: any, index: number) => {
                                  const switchFromCreateId = usecase[
                                    SWITCH_FROM_CREATE_KEY
                                  ] as string | undefined;
                                  const isCreate =
                                    getUsecaseOperationType(usecase) ===
                                    "CREATE";
                                  const showSwitchUi =
                                    isCreate || !!switchFromCreateId;
                                  const existingCandidates = showSwitchUi
                                    ? rankExistingUsecasesForSuggest(
                                        usecase,
                                        getExistingUsecasesForPage(selectedPage)
                                      )
                                    : [];
                                  const matchedExisting = switchFromCreateId
                                    ? existingCandidates.find(
                                        (ex: any) =>
                                          ex.usecase_id === usecase.usecase_id
                                      )
                                    : null;
                                  const switchDiff =
                                    matchedExisting
                                      ? analyzeUseCaseFieldDiff(
                                          stripSwitchMeta(usecase),
                                          matchedExisting
                                        )
                                      : {};

                                  return (
                                  <Paper
                                    key={
                                      switchFromCreateId ||
                                      usecase.usecase_id ||
                                      index
                                    }
                                    elevation={1}
                                    sx={{
                                      p: 2,
                                      border: selectedUsecases.has(
                                        usecase.usecase_id
                                      )
                                        ? "2px solid"
                                        : "1px solid",
                                      borderColor: selectedUsecases.has(
                                        usecase.usecase_id
                                      )
                                        ? "primary.main"
                                        : "divider",
                                      backgroundColor: selectedUsecases.has(
                                        usecase.usecase_id
                                      )
                                        ? "primary.50"
                                        : "background.paper",
                                    }}
                                  >
                                    <Box
                                      display="flex"
                                      alignItems="flex-start"
                                      gap={1}
                                    >
                                      <Checkbox
                                        checked={selectedUsecases.has(
                                          usecase.usecase_id
                                        )}
                                        onChange={(e) =>
                                          handleUsecaseSelect(
                                            usecase.usecase_id,
                                            e.target.checked
                                          )
                                        }
                                        sx={{ mt: -0.5 }}
                                      />
                                      <Box flex={1}>
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                          mb={0.5}
                                          flexWrap="wrap"
                                        >
                                          <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                          >
                                            {usecase.title ||
                                              usecase.name ||
                                              `機能 ${index + 1}`}
                                          </Typography>
                                          <Chip
                                            label={
                                              getUsecaseOperationType(usecase) ===
                                              "CREATE"
                                                ? "新規"
                                                : "更新"
                                            }
                                            size="small"
                                            color={
                                              getUsecaseOperationType(usecase) ===
                                              "CREATE"
                                                ? "success"
                                                : "warning"
                                            }
                                            variant="outlined"
                                            sx={{
                                              height: "20px",
                                              fontSize: "0.7rem",
                                            }}
                                          />
                                          {switchFromCreateId && (
                                            <Chip
                                              label="既存へ切替済"
                                              size="small"
                                              color="info"
                                              variant="outlined"
                                              sx={{
                                                height: "20px",
                                                fontSize: "0.7rem",
                                              }}
                                            />
                                          )}
                                        </Box>
                                        {showSwitchUi && (
                                          <Box
                                            mt={1}
                                            mb={1}
                                            p={1.5}
                                            sx={{
                                              backgroundColor: "grey.50",
                                              borderRadius: 1,
                                              border: "1px solid",
                                              borderColor: "divider",
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              display="block"
                                              mb={1}
                                            >
                                              誤って新規になった場合は、同一画面の既存機能を選んで更新に切り替えられます（新規のまま登録も可）。
                                            </Typography>
                                            {existingCandidates.length === 0 ? (
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                              >
                                                この画面に切替可能な既存機能がありません。
                                              </Typography>
                                            ) : (
                                              <Box
                                                display="flex"
                                                gap={1}
                                                alignItems="flex-start"
                                                flexWrap="wrap"
                                              >
                                                <FormControl
                                                  size="small"
                                                  sx={{ minWidth: 280, flex: 1 }}
                                                >
                                                  <InputLabel id={`uc-switch-${index}`}>
                                                    既存機能へ切替
                                                  </InputLabel>
                                                  <Select
                                                    labelId={`uc-switch-${index}`}
                                                    label="既存機能へ切替"
                                                    value={
                                                      switchFromCreateId
                                                        ? usecase.usecase_id
                                                        : ""
                                                    }
                                                    onChange={(e) => {
                                                      const nextId = String(
                                                        e.target.value || ""
                                                      );
                                                      const originalId =
                                                        switchFromCreateId ||
                                                        usecase.usecase_id;
                                                      if (!nextId) {
                                                        revertCreateUsecaseSwitch(
                                                          selectedPage.group_index,
                                                          originalId
                                                        );
                                                        return;
                                                      }
                                                      switchCreateUsecaseToExisting(
                                                        selectedPage.group_index,
                                                        originalId,
                                                        nextId
                                                      );
                                                    }}
                                                  >
                                                    <MenuItem value="">
                                                      <em>切替しない（新規のまま）</em>
                                                    </MenuItem>
                                                    {existingCandidates.map(
                                                      (ex: any) => (
                                                        <MenuItem
                                                          key={ex.usecase_id}
                                                          value={ex.usecase_id}
                                                        >
                                                          {ex.title ||
                                                            ex.name ||
                                                            ex.usecase_id}
                                                        </MenuItem>
                                                      )
                                                    )}
                                                  </Select>
                                                </FormControl>
                                                {switchFromCreateId && (
                                                  <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() =>
                                                      revertCreateUsecaseSwitch(
                                                        selectedPage.group_index,
                                                        switchFromCreateId
                                                      )
                                                    }
                                                  >
                                                    切替を解除
                                                  </Button>
                                                )}
                                              </Box>
                                            )}
                                            {switchFromCreateId &&
                                              Object.keys(switchDiff).length >
                                                0 && (
                                                <Box mt={1}>
                                                  <UseCaseDetailedDiff
                                                    useCase={{
                                                      fieldChanges: switchDiff,
                                                    }}
                                                  />
                                                </Box>
                                              )}
                                          </Box>
                                        )}
                                        {usecase.description && (
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            paragraph
                                          >
                                            {usecase.description}
                                          </Typography>
                                        )}
                                        {usecase.steps &&
                                          usecase.steps.length > 0 && (
                                            <Box mt={1}>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                fontWeight="bold"
                                              >
                                                ステップ:
                                              </Typography>
                                              <Box
                                                component="ol"
                                                sx={{ pl: 2, mt: 0.5 }}
                                              >
                                                {usecase.steps.map(
                                                  (
                                                    step: any,
                                                    stepIndex: number
                                                  ) => (
                                                    <Box
                                                      component="li"
                                                      key={stepIndex}
                                                      sx={{ mb: 0.5 }}
                                                    >
                                                      <Typography variant="body2">
                                                        {typeof step ===
                                                        "string"
                                                          ? step
                                                          : step.step ||
                                                            step.description ||
                                                            JSON.stringify(
                                                              step
                                                            )}
                                                      </Typography>
                                                    </Box>
                                                  )
                                                )}
                                              </Box>
                                            </Box>
                                          )}
                                      </Box>
                                    </Box>
                                  </Paper>
                                  );
                                }
                              )}
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              p: 4,
                              textAlign: "center",
                              color: "text.secondary",
                            }}
                          >
                            <Typography variant="body1">
                              この画面には機能がありません
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="h6">
                    左側から画面を選択してください
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

      </DialogContent>

      {/* 情報表示（DialogContent のスクロール領域外に配置してスクロール挙動を安定化）
          仕様書インポート (hierarchical) では非表示。操作ログ / JSON インポートでは表示。 */}
      {viewMode !== "hierarchical" && (
        <Box
          px={2}
          py={1}
          borderTop={1}
          borderColor="divider"
          bgcolor="grey.50"
          display="flex"
          gap={3}
          flexWrap="wrap"
        >
          <Typography variant="caption" color="text.secondary">
            <strong>ワークスペースID:</strong> {workspaceId}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <strong>プロジェクトID:</strong> {projectId}
          </Typography>
        </Box>
      )}

      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Box>
          {onBack && (
            <Button onClick={onBack} disabled={loading}>
              戻る
            </Button>
          )}
        </Box>
        <Box>
          <Button onClick={handleClose} disabled={loading}>
            キャンセル
          </Button>
          <Button
            onClick={handleRegister}
            variant="contained"
            disabled={
              loading ||
              (selectionStats.selected === 0 &&
                selectedUsecases.size === 0 &&
                deleteCandidateIds.size === 0)
            }
            startIcon={loading ? <CircularProgress size={20} /> : <Save />}
          >
            {loading ? "登録中..." : registerButtonLabel}
          </Button>
        </Box>
      </DialogActions>
        </>
      )}
    </Dialog>
  );
};
